import {
  Bell,
  CalendarCheck,
  Chats,
  Hourglass,
  MapPin,
  ShieldCheck,
  Star,
  UserPlus,
  Users,
  type Icon,
} from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { formatDateTime, formatRelativeTime } from '@/lib/i18n/format'

import { useNotificationsLocale } from '../hooks/use-locale'
import type { Notification, NotificationType } from '../types'

interface NotificationTileProps {
  notification: Notification
  onOpen: (notification: Notification) => void
}

const NOTIFICATION_ICONS: Record<NotificationType, Icon> = {
  friend_request: UserPlus,
  friend_request_accepted: UserPlus,
  friend_request_rejected: UserPlus,
  private_message: Chats,
  join_request: Users,
  new_participant: Users,
  join_request_accepted: Users,
  join_request_rejected: Users,
  activity_cancelled: CalendarCheck,
  activity_updated: CalendarCheck,
  activity_chat_message: Chats,
  activity_feedback_received: Star,
  activity_feedback_available: Star,
  waitlist_promoted: Hourglass,
  waitlist_turn: Hourglass,
  moderation_activity: ShieldCheck,
  nearby_activity_created: MapPin,
}

/**
 * One inbox row. Unread rows use the mint background and a coral dot; the full
 * localized timestamp is available to screen readers while sighted users see a
 * compact relative time. The whole row is the mark-read control.
 */
export function NotificationTile({
  notification,
  onOpen,
}: NotificationTileProps) {
  const { t } = useTranslation()
  const locale = useNotificationsLocale()
  const unread = notification.readAt === null
  const Icon = NOTIFICATION_ICONS[notification.type] ?? Bell
  const createdAt = notification.createdAt
  const relative = formatRelativeTime(createdAt, locale)
  const fullDate = formatDateTime(createdAt, locale)

  return (
    <button
      type="button"
      className="notification-tile"
      data-unread={unread ? 'true' : 'false'}
      onClick={() => onOpen(notification)}
    >
      <span className="notification-tile__icon" aria-hidden="true">
        <Icon size={22} weight="bold" />
      </span>

      <span className="min-w-0 flex-1 text-left">
        <span className="flex items-start gap-2">
          <span className="notification-tile__title min-w-0 flex-1">
            {notification.title}
          </span>
          {unread ? (
            <>
              <span className="notification-tile__dot" aria-hidden="true" />
              <span className="sr-only">{t('notifications.list.unread')}</span>
            </>
          ) : null}
        </span>

        <span className="mt-1 block text-[13px] leading-snug text-muted">
          {notification.body || t('notifications.list.fallbackBody')}
        </span>

        <span className="mt-2 block text-xs font-bold text-muted">
          <time dateTime={createdAt} title={fullDate}>
            {relative}
          </time>
          <span className="sr-only"> — {fullDate}</span>
        </span>
      </span>
    </button>
  )
}
